import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AddressApiResponseAddress,
  ValidationItems,
  ValidationProps,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';

import {
  getValidationConditions,
  getValidationErrors,
  validateAddressSelect,
} from 'utils';

import { BASE_FIELDNAMES } from './constants';

import { AddressFieldType } from '..';

export interface AddressFieldContextType {
  addressType: AddressFieldType;
  addresses: AddressApiResponseAddress[] | undefined;
  manual: boolean;
  setManual: React.Dispatch<React.SetStateAction<boolean>>;
  apiPostcode: string | undefined;
  postcodeName: string;
  postcodeCallback: (data: StringIndexedObject) => void;
  postcodeIsValidating: boolean;
  setPostcodeIsValidating: React.Dispatch<React.SetStateAction<boolean>>;
  selectProps: ValidationProps;
  buildingValidation: ValidationItems;
  postcodeValidation: ValidationItems;
  onPostcodeBlur: () => void;
}

export const AddressFieldContext =
  createContext<AddressFieldContextType | null>(null);

export const useAddressFieldContext = () =>
  useContext(AddressFieldContext) as AddressFieldContextType;

export interface AddressFieldPostcodeProviderProps {
  children: ReactNode;
  addressType: AddressFieldType;
}

const AddressFieldPostcodeProvider: FunctionComponent<AddressFieldPostcodeProviderProps> =
  function ({ children, addressType }) {
    const { getValues, setValue } = useFormContext();

    const [addresses, setAddresses] = useState<
      AddressApiResponseAddress[] | undefined
    >();
    const [manual, setManual] = useState<boolean>(true);
    const [apiPostcode, setApiPostcode] = useState<string | undefined>();
    const [postcodeIsValidating, setPostcodeIsValidating] =
      useState<boolean>(false);

    const baseName = useMemo(() => BASE_FIELDNAMES[addressType], [addressType]);

    const postcodeName = useMemo(() => `${baseName}.postcode`, [baseName]);

    const postcodeCallback = useCallback((results) => {
      setApiPostcode(results?.postcode);
      setAddresses(results?.addresses);
    }, []);

    const selectValidation = useMemo(
      () => ({
        validate: {
          value: () => validateAddressSelect(getValues(), baseName),
          message:
            'You must select an address or enter the address details manually',
        },
      }),
      [getValues, baseName],
    );

    const selectProps = useMemo(
      () => ({
        validationConditions: getValidationConditions(selectValidation),
        validationErrors: getValidationErrors(selectValidation),
      }),
      [selectValidation],
    );

    const [buildingName, buildingNumber, line1, selected] = useWatch({
      name: [
        `${baseName}.building_name`,
        `${baseName}.building_number`,
        `${baseName}.line_1`,
        `${baseName}.selected`,
      ],
    });

    const buildingValidation = useMemo(
      () => ({
        required: {
          value: !buildingName && !buildingNumber,
          message: 'Building name or number is required',
        },
      }),
      [buildingName, buildingNumber],
    );

    const postcodeValidation = useMemo(
      (): ValidationItems => ({
        required: { value: true, message: 'Postcode is required.' },
        pattern: {
          value:
            /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
          message: 'Postcode input is invalid',
        },
      }),
      [],
    );

    const onPostcodeBlur = useCallback(() => {
      const postcode = getValues(postcodeName) as string | undefined;
      if (!postcode) return;
      const clean = postcode.toUpperCase().replace(/\s+/g, '');
      const postcodeRegex =
        /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;
      if (!postcodeRegex.test(clean)) {
        return;
      }
      const formatted = [clean.slice(0, -3), clean.slice(-3)].join(' ');
      setValue(postcodeName, formatted);
    }, [getValues, setValue, postcodeName]);

    const values = useMemo(
      () => ({
        addressType,
        addresses,
        manual,
        setManual,
        apiPostcode,
        postcodeName,
        postcodeCallback,
        postcodeIsValidating,
        setPostcodeIsValidating,
        selectProps,
        buildingValidation,
        postcodeValidation,
        onPostcodeBlur,
      }),
      [
        addressType,
        addresses,
        manual,
        apiPostcode,
        postcodeName,
        postcodeCallback,
        postcodeIsValidating,
        setPostcodeIsValidating,
        selectProps,
        buildingValidation,
        postcodeValidation,
        onPostcodeBlur,
      ],
    );

    useEffect(() => {
      if (!line1 || !addresses || addresses?.length === 0) return;
      const match = addresses.findIndex((address) => address.line_1 === line1);
      if (match === selected) return;
      setValue(`${baseName}.selected`, match);
    }, [line1, addresses, selected, setValue, baseName]);

    return (
      <AddressFieldContext.Provider value={values}>
        {children}
      </AddressFieldContext.Provider>
    );
  };

export default AddressFieldPostcodeProvider;
